/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExecutionSummaryV2 } from '@flux/types';
import { ArrowLeft, Loader2, Eye, EyeOff, Check, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

import { getExecutionTimestamp, getExecutionDuration } from '@/lib/execution-helpers';
import { useProjectStats, useProjectExecutions } from '@/hooks/use-metrics';
import type { Project } from '@/lib/graphql/generated';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { timeAgo, cn } from '@/lib/utils';

function formatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

function estimateP95(buckets: Record<string, number> | undefined, total: number): string {
  if (!buckets || total === 0) return '0ms';
  const target = total * 0.95;
  let running = 0;
  const orderedKeys = ['0-10', '10-50', '50-100', '100-300', '300-1000', '1000+'];
  for (const k of orderedKeys) {
    running += buckets[k] ?? 0;
    if (running >= target) {
      if (k === '1000+') return '1s+';
      const [, upper] = k.split('-');
      return `${upper}ms`;
    }
  }
  return '0ms';
}

interface ChartDataEntry {
  name: string;
  value: number;
}
interface TooltipEntry {
  value: number;
  payload: ChartDataEntry;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
}): import('react/jsx-runtime').JSX.Element | null => {
  if (!(active ?? false) || payload?.length == null) return null;
  return (
    <div className="bg-[#0a0c11] text-foreground shadow-xl px-3 py-2 rounded-lg text-sm border border-white/10">
      <div className="font-medium text-[10px] uppercase font-mono text-muted-foreground/60 tracking-wider mb-1">
        Latency: {payload[0]!.payload.name} ms
      </div>
      <div className="font-semibold tabular-nums text-blue-400">
        {formatNumber(payload[0]!.value)}{' '}
        <span className="font-mono font-normal text-muted-foreground/60 text-[10px]">
          executions
        </span>
      </div>
    </div>
  );
};

function CopyButton({
  text,
  label,
}: {
  text: string;
  label?: string;
}): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(): void => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout((): void => setCopied(false), 2000);
      }}
      className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-all text-[10px] font-mono text-muted-foreground/60 hover:text-foreground"
    >
      <code>{label ?? text.slice(0, 14)}</code>
      {copied ? (
        <Check className="h-2.5 w-2.5 text-[var(--flux-success)]" />
      ) : (
        <Copy className="h-2.5 w-2.5" />
      )}
    </button>
  );
}

export function ProjectSettings({
  project,
}: {
  project: Project;
}): import('react/jsx-runtime').JSX.Element {
  const navigate = useNavigate();
  const { data: statsRaw, isFetching, isLoading } = useProjectStats(project.id!, 'LAST_24_HOURS');
  const {
    data: executions,
    isPending: execsLoading,
    error: execError,
  } = useProjectExecutions(project.id!, 'LAST_5_MIN', 10);

  const stats = useMemo(() => {
    const total = statsRaw?.total ?? 0;
    const errorCount = statsRaw?.error ?? 0;
    const errorRate = total ? (errorCount / total) * 100 : 0;
    const p95String = estimateP95(statsRaw?.latencyBuckets, total);

    return {
      total,
      success: statsRaw?.success ?? 0,
      error: errorCount,
      errorRate,
      p95String,
    };
  }, [statsRaw]);

  const [name, setName] = useState(project.name ?? '');
  const [agentUrl, setAgentUrl] = useState(project.agentUrl ?? '');
  const [localAgentUrl, setLocalAgentUrl] = useState('');
  const [builtInAgentUrl, setBuiltInAgentUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const LOCAL_AGENT_KEY = `flux_local_agent_url_${project.id}`;

  useEffect((): void => {
    const stored = localStorage.getItem(LOCAL_AGENT_KEY);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (stored != null) setLocalAgentUrl(stored);
  }, [project.id, LOCAL_AGENT_KEY]);

  useEffect((): void => {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setBuiltInAgentUrl(`${window.location.origin}/api/flux-agent`);
    }
  }, []);

  function saveLocalAgentUrl(): void {
    if (localAgentUrl.trim()) {
      localStorage.setItem(LOCAL_AGENT_KEY, localAgentUrl.trim());
    } else {
      localStorage.removeItem(LOCAL_AGENT_KEY);
    }
  }
  const [rotating, setRotating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [sdkToken, setSdkToken] = useState<string | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);

  async function revealSdkToken(): Promise<void> {
    if (sdkToken != null) {
      setSdkToken(null);
      return;
    }
    setLoadingToken(true);
    const res = await fetch(`/api/projects/${project.id}/token`);
    const data = await res.json();
    setSdkToken(data.token ?? null);
    setLoadingToken(false);
  }

  async function saveName(): Promise<void> {
    setSaving(true);
    await fetch(`/api/projects/${project.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    setSaving(false);
    window.location.reload();
  }

  async function saveAgentUrl(): Promise<void> {
    setSaving(true);
    await fetch(`/api/projects/${project.id}/agent`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentUrl }),
    });
    setSaving(false);
    window.location.reload();
  }

  async function rotateSecret(): Promise<void> {
    if (!confirm('Rotating the secret will invalidate all existing tokens. Continue?')) return;
    setRotating(true);
    setSdkToken(null); // clear cached token — it's now invalid
    await fetch(`/api/projects/${project.id}/rotate-secret`, { method: 'POST' });
    setRotating(false);
    window.location.reload();
  }

  async function testAgent(): Promise<void> {
    const urlToTest = localAgentUrl || agentUrl;
    if (!urlToTest) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(urlToTest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'ping' }),
      });
      setTestResult(res.ok ? 'Agent is reachable!' : `Error: ${res.status}`);
    } catch {
      setTestResult('Agent is not reachable');
    }
    setTesting(false);
  }

  async function deleteProject(): Promise<void> {
    if (!confirm('Are you sure you want to delete this app? Executions retained for 7 days.'))
      return;
    await fetch(`/api/projects/${project.id}`, { method: 'DELETE' });
    navigate('/projects');
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Link to="/projects" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div className="space-y-0.5">
          <h1 className="text-xl font-semibold tracking-tight">{project.name}</h1>
          <p className="text-xs text-muted-foreground">App overview and operational controls</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground mr-1">
            {isFetching && !isLoading ? 'Updating...' : 'synced'}
          </span>
        </div>
      </div>

      {/* Summary Strip & Histogram */}
      <Card className="bg-[#080a0f] border-white/5 overflow-hidden">
        {/* Top Strip */}
        <div className="flex flex-wrap items-center gap-6 py-4 px-6 border-b border-white/5 bg-[#0a0c11]">
          <div className="flex flex-col">
            <span className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground/60 mb-0.5">
              Executions
            </span>
            <span className="text-lg font-semibold tracking-tight">
              {formatNumber(stats.total)}
            </span>
          </div>

          <div className="w-px h-6 bg-white/10" />

          <div className="flex flex-col">
            <span className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground/60 mb-0.5">
              Error Rate
            </span>
            <span
              className={cn(
                'text-lg font-semibold tracking-tight',
                stats.errorRate < 1
                  ? 'text-emerald-500'
                  : stats.errorRate <= 5
                    ? 'text-yellow-500'
                    : 'text-red-500',
              )}
            >
              {stats.errorRate.toFixed(2)}%
            </span>
          </div>

          <div className="w-px h-6 bg-white/10" />

          <div className="flex flex-col">
            <span className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground/60 mb-0.5">
              p95 Latency
            </span>
            <span className="text-lg font-semibold tracking-tight">{stats.p95String}</span>
          </div>

          <div className="flex-1" />

          {/* Explicit Live indicator strictly handled structurally! */}
          {(statsRaw?.isPartial ?? false) && (
            <div className="flex items-center gap-1.5 shrink-0">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
              <span className="text-[10px] font-bold text-emerald-500 tracking-widest uppercase">
                Live
              </span>
            </div>
          )}
        </div>

        {/* Histogram Map */}
        <div className="h-[220px] p-6 pt-8 bg-gradient-to-t from-blue-900/[0.02] to-transparent">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={statsRaw?.chartData ?? []}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', opacity: 0.6 }}
                dy={10}
              />
              <Tooltip cursor={{ fill: 'rgba(255, 255, 255, 0.02)' }} content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]} isAnimationActive={false}>
                {(statsRaw?.chartData ?? []).map(
                  (
                    entry: ChartDataEntry,
                    index: number,
                  ): import('react/jsx-runtime').JSX.Element => {
                    const isSlow = entry.name === '300-1000' || entry.name === '1000+';
                    return <Cell key={`cell-${index}`} fill={isSlow ? '#2563eb' : '#3b82f6'} />;
                  },
                )}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <Link
            to={`/projects/${project.id}/executions`}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            View Executions
          </Link>
          <Link
            to={`/projects/${project.id}/executions?view=flow`}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            Open Flow View
          </Link>
          <Link
            to={`/projects/${project.id}/tests`}
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            Tests
          </Link>
        </CardContent>
      </Card>

      {/* Recent Executions Strip */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-4">
          <CardTitle className="text-sm">Recent Executions</CardTitle>
          {(execError?.message?.includes('TIMEOUT') ?? false) && (
            <span className="text-[10px] text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded-full border border-yellow-500/20 shadow-sm shadow-yellow-500/5">
              ⚠ Delayed (retrying...)
            </span>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-white/5 border-t border-white/5">
            {execsLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground/60 animate-pulse">
                Loading recent activity...
              </div>
            ) : ((executions as ExecutionSummaryV2[] | undefined)?.length ?? 0) > 0 ? (
              (executions as ExecutionSummaryV2[])
                .slice(0, 5)
                .map((e: ExecutionSummaryV2): import('react/jsx-runtime').JSX.Element => (
                  <div
                    key={e.executionId}
                    className="flex items-center justify-between p-3 px-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'w-1.5 h-1.5 rounded-full',
                          e.status === 'success'
                            ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'
                            : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]',
                        )}
                      />
                      <div>
                        <p className="text-xs font-mono text-muted-foreground">
                          <Link
                            to={`/projects/${project.id}/executions/${e.executionId}`}
                            className="hover:text-foreground transition-colors"
                          >
                            {e.executionId.slice(0, 12)}
                          </Link>
                        </p>
                        <p className="text-[10px] text-muted-foreground/60 truncate max-w-[150px] sm:max-w-xs">
                          {e.entry}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono tabular-nums">{getExecutionDuration(e)}ms</p>
                      <p className="text-[10px] text-muted-foreground/60">
                        {timeAgo(getExecutionTimestamp(e))}
                      </p>
                    </div>
                  </div>
                ))
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground/60">
                No recent executions.
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* General */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="name">App Name</Label>
              <Input id="name" value={name} onChange={(e): void => setName(e.target.value)} />
            </div>
            <Button onClick={saveName} disabled={saving} size="sm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">App ID</span>
            <code className="font-mono text-xs">{project.id}</code>
            <CopyButton text={project.id ?? ''} />
          </div>
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* SDK Token */}
          <div className="space-y-1.5">
            <Label>SDK Token</Label>
            <p className="text-xs text-muted-foreground">
              Paste as <code className="font-mono">FLUX_PROJECT_TOKEN</code> in your app. Tokens are
              signed with the HMAC secret — rotating the secret invalidates them.
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-xs break-all">
                {sdkToken ?? '••••••••••••••••••••••••••••••••••••••••'}
              </code>
              <button
                onClick={revealSdkToken}
                disabled={loadingToken}
                className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                {loadingToken ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : sdkToken != null ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
              {sdkToken != null && <CopyButton text={sdkToken} />}
            </div>
            {sdkToken != null && (
              <div className="rounded-md bg-muted p-3 mt-2">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  .env
                </p>
                <code className="font-mono text-xs break-all">{`FLUX_PROJECT_TOKEN=${sdkToken}`}</code>
                <CopyButton text={`FLUX_PROJECT_TOKEN=${sdkToken}`} />
              </div>
            )}
          </div>

          {/* HMAC Secret — not shown; users work with the SDK Token above */}
          <div className="space-y-1.5">
            <Label>HMAC Secret</Label>
            <p className="text-xs text-muted-foreground">
              The signing key used to verify SDK tokens. Never exposed directly — use the SDK Token
              above.
            </p>
          </div>

          <Button onClick={rotateSecret} disabled={rotating} variant="destructive" size="sm">
            {rotating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Rotate Secret
          </Button>
          <p className="text-xs text-muted-foreground">Rotating invalidates all existing tokens.</p>
        </CardContent>
      </Card>

      {/* Agent */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Agent</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Local / Dev URL */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="local-agent-url" className="flex items-center gap-2">
                Local / Dev URL
                <span className="text-[10px] font-normal rounded-full bg-muted px-2 py-0.5 text-muted-foreground uppercase tracking-wide">
                  This browser only
                </span>
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Your browser communicates directly with this URL for local debugging. Stored in your
                browser only — never sent to the server. Set this when running the agent locally
                (e.g. <code className="font-mono">http://localhost:3001/api/flux-agent</code>).
              </p>
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  id="local-agent-url"
                  placeholder="http://localhost:3001/api/flux-agent"
                  value={localAgentUrl}
                  onChange={(e): void => setLocalAgentUrl(e.target.value)}
                />
              </div>
              <Button onClick={saveLocalAgentUrl} size="sm" variant="outline">
                Save Locally
              </Button>
            </div>
          </div>

          <div className="border-t border-border/40" />

          {/* Project-wide URL */}
          <div className="space-y-3">
            <div>
              <Label htmlFor="agent-url">Project-wide Agent URL</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Shared across your team. Use your deployed agent URL here (recommended) for
                production replay, running test suites, and resuming failed executions. Saved to the
                project and visible to all project members.
              </p>
              {builtInAgentUrl && (
                <p className="text-xs text-muted-foreground mt-2">
                  Optional fallback: <span className="font-mono">{builtInAgentUrl}</span>.
                </p>
              )}
            </div>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Input
                  id="agent-url"
                  placeholder="https://myapp.com/api/flux-agent"
                  value={agentUrl}
                  onChange={(e): void => setAgentUrl(e.target.value)}
                />
              </div>
              <Button onClick={saveAgentUrl} disabled={saving} size="sm">
                Save
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={testAgent}
              disabled={testing || (!agentUrl && !localAgentUrl)}
              variant="outline"
              size="sm"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Test Agent Connection
            </Button>
            {testResult != null && (
              <span className="text-xs text-muted-foreground">{testResult}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Danger */}
      <Card className="border-[var(--flux-error)]/30">
        <CardHeader>
          <CardTitle className="text-sm text-[var(--flux-error)]">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={deleteProject} variant="destructive" size="sm">
            Delete App
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            Soft-deletes. Executions retained for 7 days.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
